import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { fetchAgentUrlConfig, type AgentUrlConfig, type AgentUrlFetchOptions } from '@/services/agentUrlService';

export interface UseAgentUrlOptions {
  subscriptionId: string;
  resourceGroup: string;
  appName: string;
  workflowName: string;
  hostName: string;
  isDraftMode?: boolean | undefined;
  enabled?: boolean | undefined;
}

export interface UseAgentUrlReturn {
  data: AgentUrlConfig | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch agent URL configuration for agentic workflows.
 * Returns the agent URL, API key, and OBO token needed for A2A chat.
 */
export const useAgentUrl = (options: UseAgentUrlOptions): UseAgentUrlReturn => {
  const {
    subscriptionId,
    resourceGroup,
    appName,
    workflowName,
    hostName,
    isDraftMode,
    enabled = true,
  } = options;

  const { getArmToken, isAuthenticated } = useAuth();

  const canFetch = !!(
    enabled &&
    isAuthenticated &&
    subscriptionId &&
    resourceGroup &&
    appName &&
    workflowName &&
    hostName
  );

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['agentUrl', subscriptionId, resourceGroup, appName, workflowName, hostName, isDraftMode],
    queryFn: async (): Promise<AgentUrlConfig> => {
      const accessToken = await getArmToken();
      if (!accessToken) {
        throw new Error('Failed to acquire ARM token');
      }

      const fetchOptions: AgentUrlFetchOptions = {
        subscriptionId,
        resourceGroup,
        appName,
        workflowName,
        accessToken,
        hostName,
        isDraftMode,
      };

      return fetchAgentUrlConfig(fetchOptions);
    },
    enabled: canFetch,
    staleTime: 1000 * 60 * 60, // 1 hour - API keys are valid for 24 hours
    gcTime: 1000 * 60 * 60 * 8, // 8 hours
  });

  return {
    data,
    isLoading,
    isError,
    error: error as Error | null,
    refetch,
  };
};

export type { AgentUrlConfig } from '@/services/agentUrlService';
